import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import { PayPalButton } from 'react-paypal-button-v2';
import LoadingIndicator from '@/components/LoadingIndicator';
import PayPalLogo from '@/public/payments/paypal.svg';

const paypalButtonOptions = {
  clientId: process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
  currency: 'USD',
  components: 'buttons',
  locale: 'es_ES',
  debug: process.env.NODE_ENV === 'development',
};

const paypalButtonStyle = {
  shape: 'rect',
  color: 'silver',
  layout: 'vertical',
  label: 'paypal',
  tagline: false,
};

function createPayPalOrder(order) {
  return function (_, actions) {
    return actions.order.create({
      purchase_units: [
        {
          reference_id: order.id,
          amount: {
            currency_code: 'USD',
            value: order.total,
          },
          breakdown: {
            item_total: {
              value: order.itemTotal,
            },
            shipping: {
              value: 0, // no shipping value for our products
            },
            handling: {
              value: 0, // no handling value for our products
            },
            tax_total: {
              value: order.taxTotal,
            },
            discount: {
              value: 0, // TODO: get this value from the order
            },
          },
        },
      ],
      application_context: {
        brand_name: 'Asociación Iberoamericana de Scrum',
        locale: 'es-ES',
        landing_page: 'LOGIN',
        shipping_preference: 'NO_SHIPPING',
        user_action: 'PAY_NOW',
      },
    });
  };
}

function getPayPalPayerData(payer) {
  return {
    id: payer.payer_id,
    email: payer.email_address,
    address: {
      countryCode: payer.address.country_code,
    },
    name: {
      givenName: payer.name.given_name,
      surname: payer.name.surname,
    },
  };
}

function onPayPalApproved(onPaymentEarlyAccepted, onPaymentAccepted) {
  return function (data, actions) {
    onPaymentEarlyAccepted();

    actions.order.capture().then(() => {
      let transaction = {
        orderId: data.orderID,
        payerId: data.payerID,
        payer: null,
      };

      actions.order.get(transaction.orderID).then((order) => {
        transaction = {
          ...transaction,
          id: order.id,
          status: order.status,
          payer: getPayPalPayerData(order.payer),
          createdAt: order.create_time,
          updatedAt: order.update_time,
        };

        onPaymentAccepted(transaction);
      });
    });
  };
}

function PayPalPaymentWorkflowLoadingIndicator() {
  return (
    <div className="absolute z-20 inset-0 flex items-center justify-center bg-warm-50">
      <div className="flex space-x-4 items-center relative z-10">
        <LoadingIndicator />
        <div className="font-normal text-gray-500">
          Cargando pasarela de pago PayPal
        </div>
      </div>
    </div>
  );
}

export default function PayPalPaymentWorkflow({
  checkout,
  onPaymentAccepted,
  onPaymentRejected,
  onPaymentCanceled,
}) {
  const [paymentRejectedError, setPaymentRejectedError] = useState(null);

  const [paypalIsNotReadyToUse, setPayPalNotReadyToUse] = useState(true);

  const [isProcessingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    return () => {
      if (paymentRejectedError !== null) {
        onPaymentRejected(paymentRejectedError);
      }
    };
  }, [onPaymentRejected, paymentRejectedError]);

  const onPayPalButtonsReady = useCallback(() => {
    setPayPalNotReadyToUse(false);
  }, []);

  const onPaymentEarlyApproved = useCallback(() => {
    setProcessingPayment(true);
  }, []);

  const onPaymentApproved = useCallback(
    (data) => {
      setPaymentRejectedError(null);
      onPaymentAccepted(data);
    },
    [onPaymentAccepted]
  );

  const onPaymentErrorCallback = useCallback((err) => {
    setPaymentRejectedError(err);
  }, []);

  const order = {
    id: checkout.order.id,
    itemTotal: checkout.order.subtotal,
    total: checkout.order.total,
    discount: checkout.order.discountTotal,
    taxTotal: checkout.order.totalTax,
  };

  return (
    <div className="flex justify-center w-full h-full overflow-x-hidden overflow-y-auto">
      <div className="bg-white shadow-2xl w-2/5 lg:w-2/3 flex ring-1 ring-black ring-opacity-10 relative">
        <div className="flex flex-col w-full relative z-10">
          <div className="flex items-center space-x-4 px-8 py-6 shadow-sm border-b border-gray-300 relative z-10">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-green-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex items-center space-x-4 flex-1">
              <h3 className="text-lg lg:text-2xl font-normal lg:font-semibold tracking-tight">
                Pagos seguros con
              </h3>
              <div className="w-28 -mb-1">
                <Image alt="PayPal" src={PayPalLogo} layout="responsive" />
              </div>
            </div>
          </div>
          <div className="bg-warm-100 flex-1 overflow-y-scroll p-8 relative z-0">
            {paypalIsNotReadyToUse && <PayPalPaymentWorkflowLoadingIndicator />}
            <div className="relative z-10 w-full text-center">
              <PayPalButton
                amount={200}
                createOrder={createPayPalOrder(order)}
                onApprove={onPayPalApproved(
                  onPaymentEarlyApproved,
                  onPaymentApproved
                )}
                onCancel={onPaymentCanceled}
                catchError={onPaymentErrorCallback}
                onButtonReady={onPayPalButtonsReady}
                options={paypalButtonOptions}
                style={paypalButtonStyle}
              />
            </div>
          </div>
        </div>
        {isProcessingPayment && (
          <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center bg-warm-50">
            <div className="flex items-center space-x-2">
              <LoadingIndicator />
              <div className="font-base">
                Procesando el pago a travéz de PayPal
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export const PaymentezCheckoutShape = PropTypes.shape({
  order: PropTypes.shape({
    customer: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      email: PropTypes.string,
      phone: PropTypes.string,
      total: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      totalTax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      orderNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      orderKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
}).isRequired;

PayPalPaymentWorkflow.defaultProps = {
  onPaymentAccepted: () => console.log('onPaymentAccepted() not implemented'),
  onPaymentRejected: () => console.log('onPaymentRejected() not implemented'),
  onPaymentCanceled: () => console.log('onPaymentCanceled() not implemented'),
};

PayPalPaymentWorkflow.propTypes = {
  checkout: PaymentezCheckoutShape,
  onPaymentAccepted: PropTypes.func,
  onPaymentRejected: PropTypes.func,
  onPaymentCanceled: PropTypes.func,
};
